import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import { ingredientApiService } from "services/ingredientApiService";
import DeleteModal from "pages/Calendar/DeleteModal";
import CreateIngredientModal from "./CreateIngredientModal";
import UpdateIngredientModal from "./UpdateIngredientModal";

const AllIngredients = () => {
  const navigate = useNavigate();

  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedIngredientId, setSelectedIngredientId] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [deletingIngredient, setDeletingIngredient] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  // Fetch ingredients
  const fetchIngredients = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await ingredientApiService.getAllIngredients();
      setIngredients(data);
    } catch (error) {
      toast.error("Failed to fetch ingredients");
      console.error("Error fetching ingredients:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchIngredients();
  }, []);

  const showDeleteModal = (id) => {
    setSelectedIngredientId(id);
    setDeleteModal(true);
  };

  const handleDeleteIngredient = async () => {
    setDeletingIngredient(true);
    try {
      await ingredientApiService.deleteIngredient(selectedIngredientId);
      toast.success("Ingredient deleted successfully");
      setDeleteModal(false);
      fetchIngredients(false);
    } catch (error) {
      toast.error("Failed to delete ingredient");
      console.error("Error deleting ingredient:", error);
    } finally {
      setDeletingIngredient(false);
    }
  };

  const handleUpdateIngredient = (ingredient) => {
    setSelectedIngredient(ingredient);
    setUpdateModalOpen(true);
  };

  const handleCreateSuccess = () => {
    fetchIngredients(false);
    toast.success("Ingredient created successfully");
    setCreateModalOpen(false);
  };

  const handleUpdateSuccess = () => {
    fetchIngredients(false);
    toast.success("Ingredient updated successfully");
    setUpdateModalOpen(false);
  };

  // Get unique stores for filter
  const stores = useMemo(() => {
    const uniqueStores = [...new Set(ingredients.map(ingredient => ingredient.store_name))];
    return uniqueStores.filter(store => store).sort();
  }, [ingredients]);

  // Filter ingredients based on search and store
  const filteredIngredients = useMemo(() => {
    let filtered = ingredients;

    // Apply store filter
    if (storeFilter) {
      filtered = filtered.filter(ingredient => ingredient.store_name === storeFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (ingredient) =>
          (ingredient.id && ingredient.id.toString().toLowerCase().includes(query)) ||
          (ingredient.ingredient_name && ingredient.ingredient_name.toLowerCase().includes(query)) ||
          (ingredient.store_name && ingredient.store_name.toLowerCase().includes(query)) ||
          (ingredient.note && ingredient.note.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [ingredients, storeFilter, searchQuery]);

  const formatCurrency = (amount) => {
    if (!amount) return "$0";
    return `$${parseFloat(amount).toLocaleString('en-US')}`;
  };

  const formatUnitPrice = (price, quantity, unit) => {
    if (!price || !quantity) return "-";
    const unitPrice = (parseFloat(price) / parseFloat(quantity)).toFixed(2);
    return `$${unitPrice}/${unit || 'unit'}`;
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            #{row.original.id}
          </div>
        ),
      },
      {
        header: "Name",
        accessorKey: "ingredient_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {row.original.ingredient_name || "-"}
          </div>
        ),
      },
      {
        header: "Store",
        accessorKey: "store_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            {row.original.store_name || "-"}
            {/* <small className="d-block text-truncate" style={{ maxWidth: "150px" }}>
            </small> */}
          </div>
        ),
      },
      {
        header: "Price",
        accessorKey: "price",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {formatCurrency(row.original.price)}
          </div>
        ),
      },
      {
        header: "Quantity",
        accessorKey: "quantity",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {row.original.quantity || "0"} {row.original.unit || ""}
          </div>
        ),
      },
      {
        header: "Unit Price",
        accessorKey: "unitprice",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className=" text-info">
            {row.original.unitprice || formatUnitPrice(row.original.price, row.original.quantity, row.original.unit)}
          </div>
        ),
      },
      {
        header: "Note",
        accessorKey: "note",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <small className="d-block text-truncate" style={{ maxWidth: "150px" }}>
              {row.original.note || "-"}
            </small>
          </div>
        ),
      },
      {
        header: "Image",
        accessorKey: "ingredient_image",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-center">
            {row.original.ingredient_image ? (
              <button
                onClick={() => setSelectedImage(row.original.ingredient_image)}
                className="btn btn-info btn-sm"
                title="View Image"
              >
                <i className="fas fa-eye"></i>
              </button>
            ) : (
              <span className="text-muted">-</span>
            )}
          </div>
        ),
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              onClick={() => handleUpdateIngredient(row.original)}
              className="btn btn-warning btn-sm"
              title="Edit Ingredient"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => showDeleteModal(row.original.id)}
              title="Delete Ingredient"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // Calculate statistics
  const totalIngredients = ingredients.length;
  const totalCost = ingredients.reduce((sum, ingredient) => sum + parseFloat(ingredient.price || 0), 0);
  const avgUnitPrice = totalIngredients > 0
    ? (totalCost / ingredients.reduce((sum, ingredient) => sum + parseFloat(ingredient.quantity || 0), 0)).toFixed(2)
    : 0;

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Ingredients" breadcrumbItem="All Ingredients" />

        {/* Statistics Cards */}
        {/* <div className="row mb-4">
          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Total Ingredients</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{totalIngredients}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary rounded-circle">
                        <i className="fas fa-carrot font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Total Cost</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{formatCurrency(totalCost)}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-success rounded-circle">
                        <i className="fas fa-dollar-sign font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Avg Unit Price</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">${avgUnitPrice}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-info rounded-circle">
                        <i className="fas fa-balance-scale font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Unique Stores</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{stores.length}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-warning rounded-circle">
                        <i className="fas fa-store font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Filters and Actions */}
        <div className="row mb-3">
          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3 align-items-center">

                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, store, note..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: "300px" }}
                  />
                </div>

                <div className="d-flex align-items-center gap-2">
                  {/* <label className="form-label mb-0">Store:</label> */}
                  <select
                    className="form-select"
                    style={{ width: "auto" }}
                    value={storeFilter}
                    onChange={e => setStoreFilter(e.target.value)}
                  >
                    <option value="">All Stores</option>
                    {stores.map((store, index) => (
                      <option key={index} value={store}>
                        {store}
                      </option>
                    ))}
                  </select>
                </div>



                {(storeFilter || searchQuery) && (
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setStoreFilter("");
                      setSearchQuery("");
                    }}
                  >
                    <i className="fas fa-times me-1"></i> Clear Filters
                  </button>
                )}
              </div>

              <div className="d-flex gap-2">
                <button
                  onClick={() => setCreateModalOpen(true)}
                  className="btn btn-success"
                >
                  <i className="fas fa-plus me-1"></i> Create New Ingredient
                </button>

                <button
                  className="btn btn-light"
                  onClick={() => fetchIngredients(false)}
                  disabled={refreshing}
                  title="Refresh Ingredients"
                >
                  <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Ingredients Table */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">


                <TableContainer
                  columns={columns}
                  data={filteredIngredients}
                  isGlobalFilter={false}
                  isPagination={true}
                  pagination="pagination"
                  paginationWrapper="dataTables_paginate paging_simple_numbers"
                  tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                  isCustomPageSize={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteIngredient}
          loader={deletingIngredient}
          onCloseClick={() => setDeleteModal(false)}
          title="Delete Ingredient"
          message="Are you sure you want to delete this ingredient? This action cannot be undone."
        />

        <CreateIngredientModal
          isOpen={createModalOpen}
          toggle={() => setCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />

        {selectedIngredient && (
          <UpdateIngredientModal
            isOpen={updateModalOpen}
            toggle={() => setUpdateModalOpen(false)}
            ingredient={selectedIngredient}
            onUpdateSuccess={handleUpdateSuccess}
          />
        )}

        {/* Image Viewer Modal */}
        {selectedImage && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.9)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content bg-transparent border-0">
                <div className="modal-header border-0">
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setSelectedImage(null)}
                  ></button>
                </div>
                <div className="modal-body text-center">
                  <img
                    src={selectedImage}
                    alt="Ingredient"
                    className="img-fluid"
                    style={{ maxHeight: '80vh' }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllIngredients;